import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../lib/api';
import { chime } from '../lib/sounds';
import { REASON_LABEL, STATE_COLOR, STATE_LABEL, useNova } from '../lib/store';
import type { AgentState, LiveCall, MonitorView, NovaEvent, QueueStat } from '../lib/types';
import { act, dial, formatNumber } from '../lib/actions';
import { loadReference } from '../lib/reference';

/** Drag-and-drop payload: an agent dragged from their tile onto a queue */
const AGENT_DRAG = 'application/x-nova-agent';

/** Puts an agent on a queue live (PRD SV-05): the API gives them the skills it needs. */
async function addToQueue(agentId: string, queueId: string) {
  const r = await act(() => api<{ name: string; queue: string; added: string[] }>('POST', `/agents/${agentId}/queues/${queueId}`));
  if (!r) return;
  useNova
    .getState()
    .toast(r.added.length ? `${r.name} now takes ${r.queue} calls (added ${r.added.join(', ')})` : `${r.name} already takes ${r.queue} calls`, 'success');
  // Their skills changed: the directory and transfer lists read them
  if (r.added.length) void loadReference();
}
import { Button, duration, Elapsed, Panel, StateDot, useNow } from './ui';

/** A call on hold this long raises an alert (PRD SV-04). */
const LONG_HOLD_S = 120;
/** Service level is only judged once a queue has answered this many calls today. */
const SLA_MIN_CALLS = 5;
const ALERTS_KEY = 'nova.ops.alerts';

interface Alert {
  key: string;
  text: string;
}

/** What needs a supervisor now: callers past the answer target, queues under target, long holds. */
function currentAlerts(queues: QueueStat[], calls: LiveCall[], now: number): Alert[] {
  const out: Alert[] = [];
  for (const q of queues) {
    // A queue left closed after an emergency is easy to forget
    if (q.emergency_closed) out.push({ key: `closed:${q.queue_id}`, text: `${q.name} is closed to new callers. Reopen it when ready.` });
    const waited = q.oldestEnqueuedAt ? (now - q.oldestEnqueuedAt) / 1000 : 0;
    if (q.waiting && waited > q.sla_seconds) {
      out.push({ key: `wait:${q.queue_id}:${q.oldestEnqueuedAt}`, text: `${q.name}: a caller has waited ${duration(waited)}, past the ${q.sla_seconds}s target` });
    }
    if (q.answered >= SLA_MIN_CALLS && (q.within_sla / q.answered) * 100 < q.sla_target_pct) {
      out.push({ key: `sla:${q.queue_id}`, text: `${q.name}: ${Math.round((q.within_sla / q.answered) * 100)}% answered in time today, under the ${q.sla_target_pct}% target` });
    }
  }
  for (const c of calls) {
    const held = c.onHold && c.heldSince ? (now - c.heldSince) / 1000 : 0;
    if (held > LONG_HOLD_S) {
      out.push({ key: `hold:${c.interactionId}:${c.heldSince}`, text: `${c.agentName ?? 'An agent'} has had ${c.customerName ?? formatNumber(c.customerNumber)} on hold for ${duration(held)}` });
    }
  }
  return out;
}

/** Toasts and a sound once per new alert, while switched on. */
function useAlerts(alerts: Alert[], on: boolean): void {
  const seen = useRef(new Set<string>());
  useEffect(() => {
    const keys = new Set(alerts.map((a) => a.key));
    const fresh = alerts.filter((a) => !seen.current.has(a.key));
    seen.current = keys;
    if (!on || !fresh.length) return;
    chime('alert');
    for (const a of fresh) useNova.getState().toast(a.text, 'error');
  }, [alerts, on]);
}

/** Supervisor live operations: queues, agents, live calls, what just happened. */
export function Ops() {
  const queues = useNova((s) => s.queues);
  const agents = useNova((s) => s.agents);
  const people = useNova((s) => s.people);
  const calls = useNova((s) => s.calls);
  const now = useNow();

  const roster = useMemo(
    () =>
      Object.values(people)
        .map((p) => ({ person: p, live: agents[p.id] ?? null }))
        .sort((a, b) => ORDER[a.live?.state ?? 'offline'] - ORDER[b.live?.state ?? 'offline'] || a.person.name.localeCompare(b.person.name)),
    [people, agents],
  );
  const counts = roster.reduce<Record<string, number>>((acc, r) => {
    const s = r.live?.state ?? 'offline';
    acc[s] = (acc[s] ?? 0) + 1;
    return acc;
  }, {});
  const waiting = queues.reduce((n, q) => n + q.waiting, 0);
  const oldest = queues.reduce<number | null>((m, q) => (q.oldestEnqueuedAt && (!m || q.oldestEnqueuedAt < m) ? q.oldestEnqueuedAt : m), null);
  const answered = queues.reduce((n, q) => n + q.answered, 0);
  const within = queues.reduce((n, q) => n + q.within_sla, 0);
  const live = Object.values(calls).sort((a, b) => a.startedAt - b.startedAt);
  const monitors = useNova((s) => s.monitors);
  const meId = useNova((s) => s.user?.sub);
  const alerts = useMemo(() => currentAlerts(queues, live, now), [queues, live, now]);
  const [alertsOn, setAlertsOn] = useState(() => {
    try {
      return localStorage.getItem(ALERTS_KEY) !== 'off';
    } catch {
      return true;
    }
  });
  useAlerts(alerts, alertsOn);
  const toggleAlerts = () => {
    setAlertsOn(!alertsOn);
    try {
      localStorage.setItem(ALERTS_KEY, alertsOn ? 'off' : 'on');
    } catch {
      /* remembered for this page only */
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <Headline label="Waiting now" value={String(waiting)} alert={waiting > 0} />
        <Headline label="Longest wait" value={oldest ? duration((now - oldest) / 1000) : '0:00'} alert={Boolean(oldest && now - oldest > 60_000)} />
        <Headline label="Agents available" value={`${counts.available ?? 0} of ${roster.length}`} />
        <Headline label="Answered in SLA today" value={answered ? `${Math.round((within / answered) * 100)}%` : 'No calls yet'} />
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_380px]">
        <div className="flex min-w-0 flex-col gap-4">
          <Panel className="p-5">
            <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-base font-medium">Queues</h2>
              <p className="text-xs text-ink-3">Drag an agent onto a queue to have them take its calls</p>
            </div>
            <div className="flex flex-wrap gap-6">
              {queues.map((q) => (
                <QueueOrb key={q.queue_id} q={q} now={now} />
              ))}
            </div>
          </Panel>

          <Panel className="p-5">
            <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-base font-medium">Agents</h2>
              <p className="flex flex-wrap gap-4 text-sm text-ink-2">
                {(['available', 'on_call', 'wrap_up', 'break', 'away'] as AgentState[]).map((s) => (
                  <span key={s} className="inline-flex items-center gap-1.5">
                    <StateDot state={s} size={8} /> {counts[s] ?? 0} {STATE_LABEL[s].toLowerCase()}
                  </span>
                ))}
              </p>
            </div>
            <ul className="grid gap-2.5 sm:grid-cols-2 2xl:grid-cols-3">
              {roster.map(({ person, live }) => (
                <AgentTile key={person.id} name={person.name} ext={person.extension} id={person.id} live={live} calls={calls} isMe={person.id === meId} />
              ))}
            </ul>
          </Panel>
        </div>

        <div className="flex flex-col gap-4">
          <Panel className="p-5">
            <div className="mb-3 flex items-center justify-between gap-2">
              <h2 className="text-base font-medium">Needs attention</h2>
              <button
                type="button"
                role="switch"
                aria-checked={alertsOn}
                onClick={toggleAlerts}
                className={`rounded-lg border px-2 py-1 text-xs ${alertsOn ? 'border-ion/50 text-ink' : 'border-line text-ink-3'}`}
              >
                Alert sound and pop-ups: {alertsOn ? 'on' : 'off'}
              </button>
            </div>
            {alerts.length === 0 ? (
              <p className="text-sm text-ink-2">Nothing right now. Long waits, queues under their service level and long holds show here.</p>
            ) : (
              <ul className="flex flex-col gap-1.5" aria-live="polite">
                {alerts.map((a) => (
                  <li key={a.key} className="flex gap-2 text-sm">
                    <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-break" />
                    {a.text}
                  </li>
                ))}
              </ul>
            )}
          </Panel>
          <Panel className="p-5">
            <h2 className="mb-3 text-base font-medium">Live calls</h2>
            {live.length === 0 ? (
              <p className="text-sm text-ink-2">No calls in progress.</p>
            ) : (
              <ul className="flex flex-col gap-2">
                {live.map((c) => (
                  <LiveCallRow key={c.interactionId} c={c} monitors={monitors.filter((m) => m.interactionId === c.interactionId)} meId={meId} />
                ))}
              </ul>
            )}
          </Panel>
          <Feed />
        </div>
      </div>
    </div>
  );
}

const ORDER: Record<AgentState, number> = { on_call: 0, reserved: 1, wrap_up: 2, available: 3, break: 4, away: 5, offline: 6 };

function Headline({ label, value, alert = false }: { label: string; value: string; alert?: boolean }) {
  return (
    <Panel className="px-5 py-4">
      <p className="text-sm text-ink-3">{label}</p>
      <p className={`tabular mt-1 text-3xl ${alert ? 'text-wrapup' : 'text-ink'}`}>{value}</p>
    </Panel>
  );
}

/** Orb size grows with calls waiting; the arc is today's SLA against target. */
function QueueOrb({ q, now }: { q: QueueStat; now: number }) {
  const [over, setOver] = useState(false);
  const sla = q.answered ? q.within_sla / q.answered : null;
  const ok = sla === null || sla * 100 >= q.sla_target_pct;
  const color = ok ? 'var(--color-available)' : 'var(--color-break)';
  const size = 132;
  const r = 54;
  const c = 2 * Math.PI * r;
  const core = Math.min(46, 22 + q.waiting * 6);
  const waitS = q.oldestEnqueuedAt ? (now - q.oldestEnqueuedAt) / 1000 : 0;
  const breach = waitS > q.sla_seconds;
  return (
    <figure
      className={`flex w-[150px] flex-col items-center gap-2 rounded-2xl p-1 transition ${over ? 'bg-ion/10 ring-2 ring-ion' : ''}`}
      onDragOver={(e) => {
        if (!e.dataTransfer.types.includes(AGENT_DRAG)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        setOver(false);
        const agentId = e.dataTransfer.getData(AGENT_DRAG);
        if (!agentId) return;
        e.preventDefault();
        void addToQueue(agentId, q.queue_id);
      }}
      aria-dropeffect="copy"
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={`${q.name}: ${q.waiting} waiting`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--color-line-strong)" strokeWidth="6" />
        {sla !== null ? (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={color}
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray={`${c * sla} ${c}`}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        ) : null}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={core}
          fill={q.waiting ? (breach ? 'var(--color-break)' : 'var(--color-wrapup)') : 'var(--color-hull-2)'}
          fillOpacity={q.waiting ? 0.22 : 1}
          stroke={q.waiting ? (breach ? 'var(--color-break)' : 'var(--color-wrapup)') : 'transparent'}
        />
        <text x="50%" y="50%" dy="0.35em" textAnchor="middle" fill="var(--color-ink)" style={{ font: '500 26px var(--font-mono)' }}>
          {q.waiting}
        </text>
      </svg>
      <figcaption className="text-center">
        <p className="font-medium">
          {q.name}
          {q.emergency_closed ? <span className="ml-1.5 rounded-md border border-break/50 px-1.5 py-px text-xs text-break">Closed</span> : null}
        </p>
        <p className="text-sm text-ink-3">
          {q.waiting ? `Oldest ${duration(waitS)}` : sla === null ? 'No calls yet today' : `${Math.round(sla * 100)}% in ${q.sla_seconds}s, target ${q.sla_target_pct}%`}
        </p>
        <ClosureButton q={q} />
      </figcaption>
    </figure>
  );
}

/** Emergency closure (PRD RT-04): new callers get the queue's "when closed" treatment until reopened. */
function ClosureButton({ q }: { q: QueueStat }) {
  const [asking, setAsking] = useState(false);
  useEffect(() => {
    if (!asking) return;
    const t = window.setTimeout(() => setAsking(false), 4000);
    return () => window.clearTimeout(t);
  }, [asking]);
  const set = (closed: boolean) =>
    act(() => api('PATCH', `/queues/${q.queue_id}/emergency`, { closed }), closed ? `${q.name} closed to new callers` : `${q.name} open again`);
  const base = 'mt-1.5 rounded-lg border px-2 py-0.5 text-xs';
  if (q.emergency_closed) {
    return (
      <button type="button" className={`${base} border-line text-ink-2 hover:border-available hover:text-ink`} onClick={() => void set(false)}>
        Reopen
      </button>
    );
  }
  return asking ? (
    <button type="button" className={`${base} border-break bg-break/10 text-break`} onClick={() => (setAsking(false), void set(true))}>
      Close to new callers?
    </button>
  ) : (
    <button type="button" className={`${base} border-line text-ink-3 hover:border-break hover:text-break`} onClick={() => setAsking(true)} title="New callers get this queue's closed treatment until you reopen it">
      Close now
    </button>
  );
}

function AgentTile({
  id,
  name,
  ext,
  live,
  calls,
  isMe,
}: {
  id: string;
  name: string;
  ext: string | null;
  live: import('../lib/types').AgentLive | null;
  calls: Record<string, LiveCall>;
  isMe: boolean;
}) {
  const state = live?.state ?? 'offline';
  const call = live?.interactionId ? calls[live.interactionId] : undefined;
  const force = (s: AgentState) => act(() => api('PUT', `/agents/${id}/state`, { state: s, reason: 'supervisor' }), `${name} set to ${STATE_LABEL[s]}`);
  const signOut = () => act(() => api('POST', `/agents/${id}/sign-out`).then(() => true), `${name} signed out`);
  const btn = 'rounded-lg border border-line px-2 py-1 text-xs';
  const queues = useNova((s) => s.queueList);
  return (
    <li
      draggable={Boolean(ext)}
      onDragStart={(e) => {
        e.dataTransfer.setData(AGENT_DRAG, id);
        e.dataTransfer.effectAllowed = 'copy';
      }}
      className={`group rounded-xl border border-line bg-abyss/40 p-3.5 ${ext ? 'cursor-grab active:cursor-grabbing' : ''}`}
      style={{ boxShadow: `inset 3px 0 0 ${STATE_COLOR[state]}` }}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="truncate font-medium">{name}</span>
        <span className="tabular text-xs text-ink-3">{ext}</span>
      </div>
      <div className="mt-1.5 flex items-center justify-between gap-2 text-sm">
        <span className="inline-flex items-center gap-1.5 text-ink-2">
          <StateDot state={state} size={8} pulse={state === 'reserved'} />
          {STATE_LABEL[state]}
          {live?.reason && state !== 'available' ? <span className="text-ink-3">{REASON_LABEL[live.reason] ?? live.reason}</span> : null}
        </span>
        {live ? <Elapsed since={live.since} className="text-ink-3" /> : null}
      </div>
      {call ? (
        <p className="mt-1.5 truncate text-sm text-ink-2">
          {/* On a colleague's call to them, the other side is the caller */}
          with {call.peerId === id ? call.agentName : call.customerName ?? formatNumber(call.customerNumber)}
          {call.direction === 'internal' ? <span className="text-ink-3"> · internal</span> : null}
        </p>
      ) : null}
      {state !== 'on_call' && state !== 'reserved' && !isMe ? (
        <div className="mt-2.5 flex flex-wrap gap-1.5 opacity-70 transition group-hover:opacity-100 group-focus-within:opacity-100">
          {ext && state !== 'offline' && state !== 'wrap_up' ? (
            <button type="button" className={`${btn} hover:border-ion hover:text-ion`} onClick={() => void dial(ext)} title={`Call ${name} on extension ${ext}`}>
              Call
            </button>
          ) : null}
          {state !== 'available' ? (
            <button type="button" className={`${btn} hover:border-available`} onClick={() => void force('available')}>
              Available
            </button>
          ) : null}
          {state !== 'break' && state !== 'offline' ? (
            <button type="button" className={`${btn} hover:border-break`} onClick={() => void force('break')}>
              Break
            </button>
          ) : null}
          {state !== 'away' && state !== 'offline' ? (
            <button type="button" className={`${btn} hover:border-away`} onClick={() => void force('away')}>
              Away
            </button>
          ) : null}
          {state !== 'offline' ? (
            <button type="button" className={`${btn} hover:border-break`} onClick={() => void signOut()} title="Closes their Nova page; they can sign in again">
              Sign out
            </button>
          ) : null}
        </div>
      ) : null}
      {ext && queues.length ? (
        // The same as dragging the tile onto a queue, for keyboard and touch
        <select
          aria-label={`Add ${name} to a queue`}
          className="mt-2.5 w-full rounded-lg border border-line bg-transparent px-2 py-1 text-xs text-ink-2 opacity-70 transition group-hover:opacity-100 focus:opacity-100"
          value=""
          onChange={(e) => e.target.value && void addToQueue(id, e.target.value)}
        >
          <option value="">Add to a queue…</option>
          {queues.map((q) => (
            <option key={q.id} value={q.id}>
              {q.name}
            </option>
          ))}
        </select>
      ) : null}
    </li>
  );
}

const MODES: { mode: MonitorView['mode']; label: string; hint: string }[] = [
  { mode: 'listen', label: 'Listen', hint: 'Hear the call; nobody hears you' },
  { mode: 'whisper', label: 'Whisper', hint: 'Only the agent hears you' },
  { mode: 'barge', label: 'Barge', hint: 'Join the call; both hear you' },
];

function LiveCallRow({ c, monitors, meId }: { c: LiveCall; monitors: MonitorView[]; meId?: string }) {
  const mine = monitors.find((m) => m.supervisorId === meId);
  const others = monitors.filter((m) => m.supervisorId !== meId);
  const canMonitor = c.status === 'connected' && Boolean(c.agentId) && c.agentId !== meId;
  const monitor = (mode: MonitorView['mode']) =>
    act(() => api('POST', `/calls/${c.interactionId}/monitor`, { mode }), mine ? undefined : 'Your softphone will connect you to the call');
  const label = c.status === 'connected' ? (c.onHold ? 'On hold' : 'Talking') : c.status === 'ringing' ? 'Ringing' : 'Waiting';
  const tone = c.status === 'connected' ? 'on_call' : c.status === 'ringing' ? 'reserved' : 'wrap_up';
  return (
    <li className="rounded-xl border border-line px-3.5 py-3">
      <div className="flex items-center justify-between gap-2">
        <span className="truncate font-medium">{c.customerName ?? formatNumber(c.customerNumber)}</span>
        <Elapsed since={c.answeredAt ?? c.queuedAt ?? c.startedAt} className="text-ink-2" />
      </div>
      <div className="mt-1 flex items-center justify-between gap-2 text-sm text-ink-2">
        <span className="inline-flex items-center gap-1.5">
          <StateDot state={tone} size={8} pulse={c.status === 'ringing'} />
          {label}
          {c.agentName ? ` with ${c.agentName}` : c.queueName ? ` in ${c.queueName}` : ''}
          {c.direction === 'internal' ? <span className="text-ink-3"> · internal</span> : null}
        </span>
        <Button tone="ghost" className="px-2 py-1 text-xs" onClick={() => void act(() => api('POST', `/calls/${c.interactionId}/hangup`), 'Call ended')}>
          End
        </Button>
      </div>
      {canMonitor ? (
        <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
          <div className="flex overflow-hidden rounded-lg border border-line" role="group" aria-label="Monitor this call">
            {MODES.map(({ mode, label, hint }) => (
              <button
                key={mode}
                type="button"
                title={hint}
                aria-pressed={mine?.mode === mode}
                onClick={() => void monitor(mode)}
                className={`px-2.5 py-1 text-xs transition ${mine?.mode === mode ? 'bg-ion/15 text-ink' : 'text-ink-2 hover:bg-veil/[0.05] hover:text-ink'}`}
              >
                {label}
              </button>
            ))}
          </div>
          {mine ? (
            <>
              <Button tone="ghost" className="px-2 py-1 text-xs" onClick={() => void act(() => api('DELETE', `/calls/${c.interactionId}/monitor`))}>
                Stop
              </Button>
              <span className="text-xs text-ink-3">{mine.connected ? MODES.find((m) => m.mode === mine.mode)!.hint : 'Connecting your softphone…'}</span>
            </>
          ) : null}
        </div>
      ) : null}
      {others.length ? (
        <p className="mt-1.5 text-xs text-ink-3">
          {others.map((m) => `${m.name} ${m.mode === 'listen' ? 'listening' : m.mode === 'whisper' ? 'whispering' : 'in the call'}`).join(', ')}
        </p>
      ) : null}
    </li>
  );
}

function Feed() {
  const feed = useNova((s) => s.feed);
  const people = useNova((s) => s.people);
  const lines = feed.map((e) => ({ e, text: describe(e, people) })).filter((l) => l.text);
  return (
    <Panel className="p-5">
      <h2 className="mb-3 text-base font-medium">Happening now</h2>
      {lines.length === 0 ? (
        <p className="text-sm text-ink-2">Events appear here as calls and agents move.</p>
      ) : (
        <ol className="flex max-h-[420px] flex-col gap-2 overflow-auto pr-1" aria-live="polite">
          {lines.slice(0, 40).map(({ e, text }) => (
            <li key={e.eventId} className="flex gap-3 text-sm">
              <time className="tabular shrink-0 text-ink-3">{new Date(e.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</time>
              <span className="text-ink-2">{text}</span>
            </li>
          ))}
        </ol>
      )}
    </Panel>
  );
}

function describe(e: NovaEvent, people: Record<string, { name: string }>): string | null {
  const d = e.data as Record<string, unknown>;
  const who = (d.customerName as string) || (d.customerNumber ? formatNumber(String(d.customerNumber)) : 'Caller');
  const agent = (d.agentName as string) || (d.agentId ? people[d.agentId as string]?.name : null);
  switch (e.type) {
    case 'call.queued':
      return `${who} is waiting in ${d.queueName ?? 'a queue'}`;
    case 'call.offered':
      return `${who} is ringing ${agent ?? 'an agent'}`;
    case 'call.answered':
      return `${agent ?? 'An agent'} answered ${who}`;
    case 'call.held':
      return `${agent ?? 'An agent'} put ${who} on hold`;
    case 'call.transferred':
      return `${who} was transferred`;
    case 'call.ended':
      return d.outcome === 'failed' ? `Call to ${who} did not connect` : `${who} finished with ${agent ?? 'an agent'}`;
    case 'call.abandoned':
      return `${who} hung up while waiting`;
    case 'agent.state_changed': {
      const name = people[d.userId as string]?.name ?? 'An agent';
      const s = d.state as AgentState;
      if (s === 'reserved' || s === 'on_call') return null;
      return `${name} is ${STATE_LABEL[s].toLowerCase()}${d.reason === 'missed_call' ? ' after missing a call' : ''}`;
    }
    case 'interaction.dispositioned':
      return `${agent ?? 'An agent'} closed a call as ${String(d.label ?? '').toLowerCase()}`;
    default:
      return null;
  }
}
